/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountJson,
  AmountString,
  Amounts,
  HttpStatusCode,
  PaytoType,
  TalerErrorCode,
  TalerUriAction,
  TalerUris,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  RenderAmount,
  TalerQrCode,
  useAsyncAction,
  useBankCoreApiContext,
  useTalerWalletIntegrationAPI,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { revalidateWithdrawalDetails } from "../../hooks/account.js";
import { LoggedIn, useSessionState } from "../../hooks/session.js";

import { useBankChallengeHandlerContext } from "../../context/challenge.js";
import { LoginForm } from "../LoginForm.js";
import { InputAmount } from "../PaytoWireTransferForm.js";
import { State } from "./index.js";
import {
  AbortWithdrawalDialog,
  WithdrawalDialogError,
} from "../../components/AbortWithdrawalDialog.js";
import { WithdrawalProgress } from "../WithdrawalProgress.js";
import { validateWithdrawalAmount } from "../withdrawal-amount.js";

export function InvalidPaytoView({ payto }: State.InvalidPayto) {
  const { i18n } = useTranslationContext();
  return (
    <div>
      {i18n.str`The payto URI returned by the server is invalid: "${payto}".`}
    </div>
  );
}
export function InvalidWithdrawalView({ uri }: State.InvalidWithdrawal) {
  const { i18n } = useTranslationContext();
  return (
    <div>
      {i18n.str`The withdrawal URI returned by the server is invalid: "${uri}".`}
    </div>
  );
}
export function InvalidReserveView({ reserve }: State.InvalidReserve) {
  const { i18n } = useTranslationContext();
  return (
    <div>
      {i18n.str`The reserve public key returned by the server is invalid: "${reserve}".`}
    </div>
  );
}

export function NeedConfirmationView({
  onAbort,
  onContinueLater,
  account,
  details,
  operationId,
  progressMode,
}: State.NeedConfirmation) {
  const { i18n } = useTranslationContext();
  const mfa = useBankChallengeHandlerContext();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const abortTitleRef = useRef<HTMLHeadingElement>(null);
  const abortTriggerRef = useRef<HTMLButtonElement>(null);
  const restoreAbortFocusRef = useRef(false);
  const [confirmingAbort, setConfirmingAbort] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [dialogError, setDialogError] = useState<
    { title: TranslatedString; description: TranslatedString } | undefined
  >();
  const { state: credentials } = useSessionState();
  const creds =
    credentials.status === "loggedIn" && credentials.username === account
      ? credentials
      : undefined;
  const {
    config,
    lib: { bank },
  } = useBankCoreApiContext();
  const initialAmount =
    details.amount.mode === "bank-selected" && details.amount.suggested
      ? Amounts.stringifyValue(details.amount.suggested)
      : undefined;
  const [amountInput, setAmountInput] = useState<string | undefined>(
    initialAmount,
  );

  useEffect(() => {
    setAmountInput(initialAmount);
    setAmountTouched(false);
    setDialogError(undefined);
    setConfirmingAbort(false);
  }, [initialAmount, operationId]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const preventCancel = (event: Event): void => event.preventDefault();
    dialog.addEventListener("cancel", preventCancel);
    if (mfa.pending) {
      if (dialog.open) dialog.close();
      return () => dialog.removeEventListener("cancel", preventCancel);
    }
    let cancelled = false;
    let frame: number | undefined;
    const openDialog = (): void => {
      if (cancelled) return;
      if (!dialog.isConnected) {
        frame = requestAnimationFrame(openDialog);
        return;
      }
      if (!dialog.open) dialog.showModal();
      titleRef.current?.focus();
    };
    openDialog();
    return () => {
      cancelled = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      dialog.removeEventListener("cancel", preventCancel);
      if (dialog.open) dialog.close();
    };
  }, [mfa.pending]);

  useEffect(() => {
    if (confirmingAbort) {
      abortTitleRef.current?.focus();
    } else if (restoreAbortFocusRef.current) {
      restoreAbortFocusRef.current = false;
      abortTriggerRef.current?.focus();
    }
  }, [confirmingAbort]);

  const minimum = !config.min_wire_transfer_amount
    ? undefined
    : Amounts.parse(config.min_wire_transfer_amount);
  const maximum = !config.max_wire_transfer_amount
    ? undefined
    : Amounts.parse(config.max_wire_transfer_amount);
  const wireFee = !config.wire_transfer_fees
    ? Amounts.zeroOfCurrency(config.currency)
    : Amounts.parse(config.wire_transfer_fees);
  const configAmountsValid =
    (!config.min_wire_transfer_amount || !!minimum) &&
    (!config.max_wire_transfer_amount || !!maximum) &&
    (!config.wire_transfer_fees || !!wireFee) &&
    [minimum, maximum, wireFee]
      .filter((amount): amount is AmountJson => amount !== undefined)
      .every(
        (amount) =>
          amount.currency.toUpperCase() === config.currency.toUpperCase(),
      );

  const trimmedAmount = amountInput?.trim();
  const editableAmount = !trimmedAmount
    ? undefined
    : Amounts.parse(`${config.currency}:${trimmedAmount}`);
  const selectedAmount =
    details.amount.mode === "fixed"
      ? details.amount.value
      : details.amount.mode === "bank-selected"
        ? editableAmount
        : undefined;
  const amountValidation =
    !editableAmount || !configAmountsValid
      ? undefined
      : validateWithdrawalAmount(editableAmount, minimum, maximum);
  const amountError =
    details.amount.mode !== "bank-selected" || !amountTouched
      ? undefined
      : !trimmedAmount
        ? i18n.str`Enter an amount.`
        : !editableAmount
          ? i18n.str`Enter a valid amount.`
          : amountValidation === "zero"
            ? i18n.str`The amount must be greater than zero.`
            : amountValidation === "below-minimum"
              ? i18n.str`The amount is below the bank's minimum.`
              : amountValidation === "above-maximum"
                ? i18n.str`The amount is above the bank's maximum.`
                : undefined;

  const expectsTalerBank =
    config.wire_type === "X_TALER_BANK" || config.wire_type === "x-taler-bank";
  const supportedAccount = expectsTalerBank
    ? details.account.targetType === PaytoType.TalerBank
    : details.account.targetType === PaytoType.IBAN;
  const missingAmount = details.amount.mode === "missing";
  const invalidConfiguration =
    !configAmountsValid ||
    wireFee === undefined ||
    (selectedAmount !== undefined &&
      selectedAmount.currency.toUpperCase() !== config.currency.toUpperCase());
  let totalDebit: AmountJson | undefined;
  if (selectedAmount && wireFee && !invalidConfiguration) {
    const total = Amounts.add(selectedAmount, wireFee);
    if (!total.saturated) totalDebit = total.amount;
  }
  const canConfirm =
    !!creds &&
    supportedAccount &&
    !missingAmount &&
    !invalidConfiguration &&
    !!selectedAmount &&
    !amountValidation &&
    !!totalDebit;

  function fail(title: TranslatedString, description: TranslatedString): void {
    setDialogError({ title, description });
  }

  // i18n.str`abort withdrawal`,
  const abort = useAsyncAction<
    Awaited<ReturnType<typeof bank.abortWithdrawalById>>,
    [LoggedIn]
  >((ct, creds: LoggedIn) => bank.abortWithdrawalById(creds, operationId), {
    onResult: (result) => {
      if (result.type === "ok") {
        onAbort();
        return;
      }
      switch (result.case) {
        case HttpStatusCode.BadRequest:
          fail(
            i18n.str`Failed to abort the withdrawal.`,
            i18n.str`The server did not understand the request.`,
          );
          return;
        case HttpStatusCode.NotFound:
          fail(
            i18n.str`Failed to abort the withdrawal.`,
            i18n.str`The operation was not found.`,
          );
          void revalidateWithdrawalDetails();
          return;
        case HttpStatusCode.Conflict:
          fail(
            i18n.str`Failed to abort the withdrawal.`,
            i18n.str`The withdrawal operation has already been confirmed and cannot be aborted.`,
          );
          void revalidateWithdrawalDetails();
          return;
        default:
          assertUnreachable(result);
      }
    },
    onError: () =>
      fail(
        i18n.str`Failed to abort the withdrawal.`,
        i18n.str`The bank could not be reached. Please try again.`,
      ),
  });

  // i18n.str`confirm withdrawal`,
  const confirm = useAsyncAction<
    Awaited<ReturnType<typeof bank.confirmWithdrawalById>>,
    [LoggedIn, amount?: AmountString, challengeIds?: string[]]
  >(
    (ct, creds: LoggedIn, amount?: AmountString, challengeIds?: string[]) =>
      bank.confirmWithdrawalById(creds, amount ? { amount } : {}, operationId, {
        challengeIds,
      }),
    {
      onResult: (result, currentCreds, amount) => {
        if (result.type === "ok") {
          mfa.cancel();
          onAbort();
          return;
        }
        switch (result.case) {
          case HttpStatusCode.Accepted:
            mfa.onNewChallenge(
              i18n.str`Withdrawal confirmation`,
              currentCreds.username,
              result.body,
              {
                running: confirm.running,
                cancel: confirm.cancel,
                run: (challengeIds) =>
                  confirm.run(currentCreds, amount, challengeIds),
              },
            );
            return;
          case HttpStatusCode.BadRequest:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The server did not understand the request.`,
            );
            return;
          case HttpStatusCode.NotFound:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The operation was not found.`,
            );
            void revalidateWithdrawalDetails();
            return;
          case TalerErrorCode.BANK_UNALLOWED_DEBIT:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The account does not have sufficient funds or the amount is outside the limits.`,
            );
            return;
          case TalerErrorCode.BANK_CONFIRM_ABORT_CONFLICT:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The withdrawal has been aborted and cannot be confirmed.`,
            );
            void revalidateWithdrawalDetails();
            return;
          case TalerErrorCode.BANK_CONFIRM_INCOMPLETE:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The wallet has not finished selecting an exchange account.`,
            );
            void revalidateWithdrawalDetails();
            return;
          case TalerErrorCode.BANK_AMOUNT_DIFFERS:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`The withdrawal amount changed. Review the current amount and try again.`,
            );
            void revalidateWithdrawalDetails();
            return;
          case TalerErrorCode.BANK_AMOUNT_REQUIRED:
            fail(
              i18n.str`Failed to confirm the withdrawal.`,
              i18n.str`Enter an amount before confirming the withdrawal.`,
            );
            return;
          default:
            assertUnreachable(result);
        }
      },
      onError: () => {
        mfa.cancel();
        fail(
          i18n.str`Failed to confirm the withdrawal.`,
          i18n.str`The bank could not be reached. Please try again.`,
        );
      },
    },
  );

  const busy = confirm.running || abort.running;
  const exchangeName =
    details.account.params["receiver-name"] ?? i18n.str`Selected exchange`;
  const confirmationAmount =
    details.amount.mode === "bank-selected" && selectedAmount
      ? (Amounts.stringify(selectedAmount) as AmountString)
      : undefined;

  async function submitConfirmation(): Promise<void> {
    if (!canConfirm || !creds) return;
    setAmountTouched(true);
    setDialogError(undefined);
    await confirm.run(creds, confirmationAmount);
  }

  const authentication = !creds ? (
    <section class="w-full max-w-lg rounded-xl bg-white p-6 text-onBackground shadow-xl">
      <WithdrawalProgress current={3} mode={progressMode} />
      <h2
        ref={titleRef}
        tabIndex={-1}
        id="withdrawal-confirmation-title"
        class="mt-5 text-lg font-semibold outline-none"
      >
        <i18n.Translate>Sign in to confirm the withdrawal</i18n.Translate>
      </h2>
      <p
        id="withdrawal-confirmation-description"
        class="mt-2 text-sm text-gray-600"
      >
        <i18n.Translate>
          Sign in as {account} before reviewing and confirming this withdrawal.
        </i18n.Translate>
      </p>
      {credentials.status === "loggedIn" ? (
        <div class="mt-4 rounded-md border border-warning/60 bg-warningContainer px-3 py-2 text-sm text-onWarningContainer">
          <i18n.Translate>
            You are signed in as {credentials.username}, but this withdrawal
            belongs to {account}.
          </i18n.Translate>
        </div>
      ) : undefined}
      <div class="mt-5">
        <LoginForm currentUser={account} fixedUser />
      </div>
      {onContinueLater ? (
        <div class="mt-4 border-t border-onBackground/10 pt-4 text-right">
          <button
            type="button"
            class="rounded-md px-3 py-2 text-sm font-semibold text-onBackground hover:bg-gray-100"
            onClick={onContinueLater}
          >
            <i18n.Translate>Finish later</i18n.Translate>
          </button>
        </div>
      ) : undefined}
    </section>
  ) : undefined;

  const confirmation = creds ? (
    <form
      class="w-full max-w-2xl overflow-hidden rounded-xl bg-white text-onBackground shadow-xl"
      autoCapitalize="none"
      autoCorrect="off"
      onSubmit={(event) => {
        event.preventDefault();
        void submitConfirmation();
      }}
    >
      <div class="px-5 py-5 sm:px-7 sm:py-6">
        <WithdrawalProgress current={3} mode={progressMode} />
        <h2
          ref={titleRef}
          tabIndex={-1}
          id="withdrawal-confirmation-title"
          class="mt-5 text-lg font-semibold outline-none"
        >
          <i18n.Translate>Confirm wallet withdrawal</i18n.Translate>
        </h2>
        <p
          id="withdrawal-confirmation-description"
          class="mt-2 text-sm text-gray-600"
        >
          <i18n.Translate>
            Review how much will leave your bank account before confirming.
          </i18n.Translate>
        </p>

        {dialogError ? (
          <div
            class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            <div class="font-semibold">{dialogError.title}</div>
            <p class="mt-1">{dialogError.description}</p>
          </div>
        ) : undefined}

        {!supportedAccount ? (
          <div
            class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            <div class="font-semibold">
              <i18n.Translate>Unsupported exchange account</i18n.Translate>
            </div>
            <p class="mt-1">
              <i18n.Translate>
                The exchange account does not use the transfer type configured
                by this bank. This withdrawal cannot be confirmed.
              </i18n.Translate>
            </p>
          </div>
        ) : undefined}
        {missingAmount ? (
          <div
            class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            <div class="font-semibold">
              <i18n.Translate>Withdrawal amount missing</i18n.Translate>
            </div>
            <p class="mt-1">
              <i18n.Translate>
                The bank did not provide an amount for this withdrawal. It
                cannot be confirmed.
              </i18n.Translate>
            </p>
          </div>
        ) : undefined}
        {invalidConfiguration || (selectedAmount && !totalDebit) ? (
          <div
            class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            <div class="font-semibold">
              <i18n.Translate>Bank amount configuration invalid</i18n.Translate>
            </div>
            <p class="mt-1">
              <i18n.Translate>
                The withdrawal amount and bank fee could not be combined safely.
                This withdrawal cannot be confirmed.
              </i18n.Translate>
            </p>
          </div>
        ) : undefined}

        <div class="mt-5 rounded-xl border border-onBackground/10 bg-background px-4 py-5 text-center">
          <div class="text-sm font-medium text-gray-600">
            <i18n.Translate>Wallet receives</i18n.Translate>
          </div>
          {details.amount.mode === "bank-selected" ? (
            <div class="mx-auto mt-3 max-w-xs text-left">
              <div
                id="withdrawal-amount-label"
                class="block text-sm font-medium text-onBackground"
              >
                <i18n.Translate>Amount to your Taler Wallet</i18n.Translate>
              </div>
              <InputAmount
                currency={config.currency}
                name="amount"
                ariaLabelledby="withdrawal-amount-label"
                left
                value={amountInput}
                onChange={(value) => {
                  setAmountInput(value);
                  setAmountTouched(true);
                  setDialogError(undefined);
                }}
              />
              {amountError ? (
                <p class="mt-2 text-sm text-red-700" role="alert">
                  {amountError}
                </p>
              ) : undefined}
              {configAmountsValid && (minimum || maximum) ? (
                <p class="mt-2 text-xs text-gray-600">
                  {minimum && maximum ? (
                    <Fragment>
                      <i18n.Translate>Allowed amount</i18n.Translate>:{" "}
                      <RenderAmount
                        value={minimum}
                        spec={config.currency_specification}
                      />{" "}
                      –{" "}
                      <RenderAmount
                        value={maximum}
                        spec={config.currency_specification}
                      />
                    </Fragment>
                  ) : minimum ? (
                    <Fragment>
                      <i18n.Translate>Minimum amount</i18n.Translate>:{" "}
                      <RenderAmount
                        value={minimum}
                        spec={config.currency_specification}
                      />
                    </Fragment>
                  ) : maximum ? (
                    <Fragment>
                      <i18n.Translate>Maximum amount</i18n.Translate>:{" "}
                      <RenderAmount
                        value={maximum}
                        spec={config.currency_specification}
                      />
                    </Fragment>
                  ) : undefined}
                </p>
              ) : undefined}
            </div>
          ) : selectedAmount ? (
            <div class="mt-1 text-3xl font-bold text-brand">
              <RenderAmount
                value={selectedAmount}
                spec={config.currency_specification}
              />
            </div>
          ) : (
            <div class="mt-1 text-sm font-semibold text-red-700">
              <i18n.Translate>Amount unavailable</i18n.Translate>
            </div>
          )}
        </div>

        <dl class="mt-5 divide-y divide-onBackground/10 text-sm">
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3">
            <dt class="font-medium text-gray-600">
              <i18n.Translate>From</i18n.Translate>
            </dt>
            <dd class="text-right font-medium break-words">{account}</dd>
          </div>
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3">
            <dt class="font-medium text-gray-600">
              <i18n.Translate>To</i18n.Translate>
            </dt>
            <dd class="text-right font-medium">
              <i18n.Translate>Taler Wallet</i18n.Translate>
            </dd>
          </div>
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3">
            <dt class="font-medium text-gray-600">
              {/* Translators: GNU Taler Exchange service, not a stock or currency exchange. */}
              <i18n.Translate>Exchange</i18n.Translate>
            </dt>
            <dd class="text-right font-medium break-words">{exchangeName}</dd>
          </div>
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3">
            <dt class="font-medium text-gray-600">
              <i18n.Translate>Bank fee</i18n.Translate>
            </dt>
            <dd class="text-right font-medium">
              {wireFee && Amounts.isZero(wireFee) ? (
                <i18n.Translate>No fee</i18n.Translate>
              ) : wireFee ? (
                <RenderAmount
                  value={wireFee}
                  spec={config.currency_specification}
                />
              ) : (
                <i18n.Translate>Unavailable</i18n.Translate>
              )}
            </dd>
          </div>
          <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3 text-base">
            <dt class="font-semibold">
              <i18n.Translate>Total debited</i18n.Translate>
            </dt>
            <dd class="text-right font-bold">
              {totalDebit ? (
                <RenderAmount
                  value={totalDebit}
                  spec={config.currency_specification}
                />
              ) : (
                <i18n.Translate>Unavailable</i18n.Translate>
              )}
            </dd>
          </div>
        </dl>

        {supportedAccount ? (
          <details class="mt-4 rounded-lg border border-onBackground/10 px-4 py-3 text-sm">
            <summary class="cursor-pointer font-semibold">
              <i18n.Translate>Exchange details</i18n.Translate>
            </summary>
            <dl class="mt-3 space-y-3">
              {details.account.targetType === PaytoType.IBAN ? (
                <div>
                  <dt class="text-gray-600">
                    <i18n.Translate>Exchange IBAN</i18n.Translate>
                  </dt>
                  <dd class="mt-1 break-all font-medium">
                    {details.account.iban}
                  </dd>
                </div>
              ) : details.account.targetType === PaytoType.TalerBank ? (
                <Fragment>
                  <div>
                    <dt class="text-gray-600">
                      <i18n.Translate>Bank hostname</i18n.Translate>
                    </dt>
                    <dd class="mt-1 break-all font-medium">
                      {details.account.host}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-gray-600">
                      <i18n.Translate>Exchange account</i18n.Translate>
                    </dt>
                    <dd class="mt-1 break-all font-medium">
                      {details.account.account}
                    </dd>
                  </div>
                </Fragment>
              ) : undefined}
            </dl>
          </details>
        ) : undefined}
      </div>

      <div class="border-t border-onBackground/10 px-5 py-4 sm:px-7">
        <p class="text-sm text-gray-600">
          <i18n.Translate>
            No money is withdrawn until you confirm. You can finish this later
            from your account overview.
          </i18n.Translate>
        </p>
        <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
          {onContinueLater ? (
            <button
              type="button"
              class="w-full rounded-md px-3 py-2 text-sm font-semibold text-onBackground hover:bg-gray-100 disabled:cursor-default disabled:opacity-50 sm:w-auto"
              disabled={busy}
              onClick={onContinueLater}
            >
              <i18n.Translate>Finish later</i18n.Translate>
            </button>
          ) : undefined}
          <AsyncButton
            submit
            name="confirm withdrawal"
            class="w-full cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white disabled:cursor-default disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:w-auto"
            disabled={!canConfirm || busy}
            onClick={(event) => {
              event.preventDefault();
              return submitConfirmation();
            }}
          >
            <i18n.Translate>Confirm withdrawal</i18n.Translate>
          </AsyncButton>
        </div>
        <div class="mt-4 border-t border-onBackground/10 pt-4">
          <button
            ref={abortTriggerRef}
            type="button"
            name="abort withdrawal"
            class="text-sm font-semibold text-red-700 hover:text-red-800 disabled:cursor-default disabled:opacity-50"
            disabled={busy}
            onClick={() => {
              setDialogError(undefined);
              setConfirmingAbort(true);
            }}
          >
            <i18n.Translate>Abort withdrawal</i18n.Translate>
          </button>
        </div>
      </div>
    </form>
  ) : undefined;

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby="withdrawal-confirmation-title"
      aria-describedby="withdrawal-confirmation-description"
      class="fixed inset-0 z-20 size-auto max-h-none max-w-none overflow-y-auto bg-transparent p-4 backdrop:bg-secondary/45"
    >
      <div class="flex min-h-full items-center justify-center">
        {confirmingAbort ? (
          <section class="w-full max-w-md rounded-xl bg-white p-6 text-onBackground shadow-xl">
            <h2
              ref={abortTitleRef}
              tabIndex={-1}
              id="withdrawal-confirmation-title"
              class="text-lg font-semibold outline-none"
            >
              <i18n.Translate>Abort this withdrawal?</i18n.Translate>
            </h2>
            <p
              id="withdrawal-confirmation-description"
              class="mt-3 text-sm text-gray-600"
            >
              <i18n.Translate>
                This stops the withdrawal. No money will be transferred, and
                this operation cannot be resumed.
              </i18n.Translate>
            </p>
            {dialogError ? (
              <div
                class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                role="alert"
              >
                <div class="font-semibold">{dialogError.title}</div>
                <p class="mt-1">{dialogError.description}</p>
              </div>
            ) : undefined}
            <div class="mt-6 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                class="rounded-md px-3 py-2 text-sm font-semibold text-onBackground hover:bg-gray-100 disabled:cursor-default disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  restoreAbortFocusRef.current = true;
                  setConfirmingAbort(false);
                  setDialogError(undefined);
                }}
              >
                <i18n.Translate>Keep withdrawal</i18n.Translate>
              </button>
              <AsyncButton
                name="confirm abort withdrawal"
                class="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-800"
                disabled={!creds || busy}
                onClick={() => {
                  setDialogError(undefined);
                  return abort.run(creds!);
                }}
              >
                <i18n.Translate>Abort withdrawal</i18n.Translate>
              </AsyncButton>
            </div>
          </section>
        ) : (
          (authentication ?? confirmation)
        )}
      </div>
    </dialog>
  );
}
export function FailedView({ error }: State.Failed) {
  const { i18n } = useTranslationContext();
  switch (error.case) {
    case HttpStatusCode.Unauthorized:
      return (
        <Attention
          type="danger"
          title={i18n.str`Unauthorized to make the operation, maybe the session has expired or the password changed.`}
        >
          {!error.detail ? undefined : (
            <div class="mt-2 text-sm text-red-700">{error.detail.hint}</div>
          )}
        </Attention>
      );
    case HttpStatusCode.Conflict:
      return (
        <Attention
          type="danger"
          title={i18n.str`The operation was rejected due to insufficient funds.`}
        >
          {!error.detail ? undefined : (
            <div class="mt-2 text-sm text-red-700">{error.detail.hint}</div>
          )}
        </Attention>
      );
    case HttpStatusCode.NotFound:
      return (
        <Attention
          type="danger"
          title={i18n.str`The operation was rejected due to insufficient funds.`}
        >
          {!error.detail ? undefined : (
            <div class="mt-2 text-sm text-red-700">{error.detail.hint}</div>
          )}
        </Attention>
      );
    default:
      assertUnreachable(error);
  }
}

export function AbortedView() {
  const { i18n } = useTranslationContext();
  return (
    <div>
      <i18n.Translate>Withdrawal aborted</i18n.Translate>
    </div>
  );
}

export function ConfirmedView({ routeClose }: State.Confirmed) {
  const { i18n } = useTranslationContext();
  return (
    <section
      class="mx-auto w-full max-w-lg overflow-hidden rounded-xl bg-white text-onBackground shadow-xl"
      aria-labelledby="withdrawal-confirmed-title"
    >
      <div class="px-6 py-8 text-center sm:px-8 sm:py-10">
        <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <svg
            class="h-7 w-7 text-green-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
        <div class="mt-5">
          <h1
            class="text-xl font-semibold leading-7"
            id="withdrawal-confirmed-title"
          >
            <i18n.Translate>Withdrawal confirmed</i18n.Translate>
          </h1>
          <p class="mx-auto mt-3 max-w-sm text-sm leading-6 text-gray-600">
            <i18n.Translate>
              The bank transfer to the Taler exchange has been initiated. Your
              Taler Wallet will receive the withdrawn amount shortly.
            </i18n.Translate>
          </p>
        </div>
      </div>
      <div class="border-t border-onBackground/10 bg-background px-6 py-4 sm:flex sm:justify-end sm:px-8">
        <a
          href={routeClose.url({})}
          name="back to account"
          class="inline-flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:w-auto"
        >
          <i18n.Translate>Back to account</i18n.Translate>
        </a>
      </div>
    </section>
  );
}

export function ReadyView({
  uri,
  amount,
  progressMode,
  onAbort,
  operationId,
  routeClose,
}: State.Ready): VNode {
  const { i18n } = useTranslationContext();
  const { publishTalerAction } = useTalerWalletIntegrationAPI();
  const [confirmAbort, setConfirmAbort] = useState(false);
  const [dialogError, setDialogError] = useState<WithdrawalDialogError>();
  const abortTriggerRef = useRef<HTMLButtonElement>(null);

  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const {
    config,
    lib: { bank },
  } = useBankCoreApiContext();

  const talerWithdrawUri = TalerUris.stringify({
    type: TalerUriAction.Withdraw,
    bankIntegrationApiBaseUrl: uri.bankIntegrationApiBaseUrl,
    withdrawalOperationId: uri.withdrawalOperationId,
  });
  const integrationBaseUrl = uri.bankIntegrationApiBaseUrl;
  const withdrawalOperationId = uri.withdrawalOperationId;
  useEffect(() => {
    publishTalerAction({
      type: TalerUriAction.Withdraw,
      bankIntegrationApiBaseUrl: integrationBaseUrl,
      withdrawalOperationId,
    });
  }, [integrationBaseUrl, publishTalerAction, withdrawalOperationId]);

  function fail(description: string): void {
    setDialogError({
      title: i18n.str`Failed to abort the withdrawal.`,
      description,
    });
  }

  // i18n.str`abort withdrawal`,
  const abort = useAsyncAction<
    Awaited<ReturnType<typeof bank.abortWithdrawalById>>,
    [LoggedIn]
  >((ct, auth: LoggedIn) => bank.abortWithdrawalById(auth, operationId), {
    onResult: (result) => {
      if (result.type === "ok") {
        setConfirmAbort(false);
        onAbort();
        return;
      }
      switch (result.case) {
        case HttpStatusCode.BadRequest:
          fail(i18n.str`The server did not understand the request.`);
          return;
        case HttpStatusCode.NotFound:
          fail(i18n.str`The operation was not found.`);
          void revalidateWithdrawalDetails();
          return;
        case HttpStatusCode.Conflict:
          fail(
            i18n.str`The withdrawal operation has already been confirmed and cannot be aborted.`,
          );
          void revalidateWithdrawalDetails();
          return;
        default:
          assertUnreachable(result);
      }
    },
    onError: () =>
      fail(i18n.str`The bank could not be reached. Please try again.`),
  });

  return (
    <Fragment>
      <section
        class="mx-auto max-w-3xl overflow-hidden bg-white shadow-xl sm:rounded-xl"
        aria-labelledby="wallet-withdrawal-title"
      >
        <div class="px-5 py-7 text-center sm:px-8 sm:py-9">
          <WithdrawalProgress
            current={progressMode === "amount-first" ? 2 : 1}
            mode={progressMode}
          />
          <h1
            id="wallet-withdrawal-title"
            class="mt-6 text-xl font-semibold text-brand sm:text-2xl"
          >
            <i18n.Translate>
              Complete withdrawal with your Taler Wallet
            </i18n.Translate>
          </h1>
          <p class="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-600">
            <i18n.Translate>
              Scan this QR code with your Taler Wallet. Your wallet will select
              an exchange and prepare the withdrawal.
            </i18n.Translate>
          </p>

          {amount ? (
            <div class="mx-auto mt-6 max-w-sm rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <div class="text-sm font-medium text-secondary">
                {amount.mode === "fixed" ? (
                  <i18n.Translate>Withdrawal amount</i18n.Translate>
                ) : (
                  <i18n.Translate>Suggested amount</i18n.Translate>
                )}
              </div>
              <div class="mt-1 text-2xl font-semibold text-brand">
                <RenderAmount
                  value={amount.value}
                  spec={config.currency_specification}
                />
              </div>
              {amount.mode === "suggested" ? (
                <p class="mt-1 text-xs text-gray-600">
                  <i18n.Translate>
                    You can change this amount in your wallet.
                  </i18n.Translate>
                </p>
              ) : undefined}
            </div>
          ) : undefined}

          <div class="mt-6 flex justify-center">
            <TalerQrCode
              url={talerWithdrawUri}
              size={360}
              alt={i18n.str`QR code to continue the withdrawal in a Taler Wallet.`}
            />
          </div>

          <div class="mt-6">
            <a
              href={talerWithdrawUri}
              name="withdraw"
              class="inline-flex items-center justify-center rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primaryDark hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <i18n.Translate>Open Taler Wallet manually</i18n.Translate>
            </a>
          </div>

          <p class="mt-4 text-sm text-gray-600">
            <i18n.Translate>Don't have a Taler Wallet?</i18n.Translate>{" "}
            <a
              class="font-semibold text-primaryDark hover:text-onPrimaryContainer"
              name="wallet page"
              href="https://taler.net/en/wallet.html"
              target="_blank"
              rel="noreferrer noopener"
            >
              <i18n.Translate>Get a wallet</i18n.Translate>
            </a>
          </p>

          <p class="mx-auto mt-6 max-w-xl border-t border-onBackground/10 pt-5 text-sm leading-6 text-gray-600">
            <i18n.Translate>
              No money will be transferred until you return here to review and
              confirm the withdrawal.
            </i18n.Translate>
          </p>
        </div>

        <div class="border-t border-onBackground/10 bg-background px-5 py-4 text-left sm:px-8">
          {creds ? (
            <button
              ref={abortTriggerRef}
              type="button"
              name="abort withdrawal"
              class="text-sm font-semibold text-red-700 hover:text-red-800"
              onClick={() => {
                setDialogError(undefined);
                setConfirmAbort(true);
              }}
            >
              <i18n.Translate>Abort withdrawal</i18n.Translate>
            </button>
          ) : (
            <a
              href={routeClose.url({})}
              class="text-sm font-semibold text-red-700 hover:text-red-800"
            >
              <i18n.Translate>Sign in to abort this withdrawal</i18n.Translate>
            </a>
          )}
        </div>
      </section>

      {confirmAbort ? (
        <AbortWithdrawalDialog
          running={abort.running}
          disabled={!creds || abort.running}
          error={dialogError}
          returnFocus={abortTriggerRef}
          onKeep={() => {
            setDialogError(undefined);
            setConfirmAbort(false);
          }}
          onAbort={() => abort.run(creds!)}
        />
      ) : undefined}
    </Fragment>
  );
}
